import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Ticket, Users, Shield, DollarSign, Target, Clock, Award, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventStats({ eventId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [eventId]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportKPI = () => {
    if (!stats) return;
    
    const kpiData = [
      ['KPI', 'Valore'],
      ['Biglietti Venduti', stats.total_sold],
      ['Biglietti Disponibili', stats.total_available],
      ['% Vendita', `${((stats.total_sold / stats.total_available) * 100).toFixed(1)}%`],
      ['Revenue Totale', `€${stats.total_revenue.toFixed(2)}`],
      ['Revenue Medio/Biglietto', `€${stats.total_sold > 0 ? (stats.total_revenue / stats.total_sold).toFixed(2) : '0'}`],
      ['Check-in Effettuati', stats.total_checkins],
      ['Check-in Rate', `${stats.total_sold > 0 ? ((stats.total_checkins / stats.total_sold) * 100).toFixed(1) : '0'}%`],
      ['Tipologia Più Venduta', stats.ticket_types.reduce((max, tt) => tt.quantity_sold > (max?.quantity_sold || 0) ? tt : max, null)?.name || 'N/A'],
      [''],
      ['Vendite per Tipologia', ''],
      ...stats.ticket_types.map(tt => [tt.name, `${tt.quantity_sold} biglietti - €${(tt.price * tt.quantity_sold).toFixed(2)}`]),
      [''],
      ['Check-in per Zona', ''],
      ...Object.entries(stats.checkins_by_zone).map(([zone, count]) => [zone, count])
    ];
    
    const csv = kpiData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kpi-evento-${eventId}-${Date.now()}.csv`;
    a.click();
    toast.success('KPI esportati!');
  };

  if (loading) {
    return <div className="text-center py-8">Caricamento statistiche...</div>;
  }

  if (!stats) {
    return <div className="text-center py-8">Nessuna statistica disponibile</div>;
  }

  const soldOutPercentage = stats.total_available > 0 ? (stats.total_sold / stats.total_available) * 100 : 0;
  const avgTicketPrice = stats.total_sold > 0 ? stats.total_revenue / stats.total_sold : 0;
  const checkinRate = stats.total_sold > 0 ? (stats.total_checkins / stats.total_sold) * 100 : 0;
  const bestSeller = stats.ticket_types.reduce((max, tt) => tt.quantity_sold > (max?.quantity_sold || 0) ? tt : max, null);
  const projectedRevenue = stats.total_available > 0 ? (stats.total_revenue / stats.total_sold) * stats.total_available : 0;

  return (
    <div className="space-y-6" data-testid="event-stats">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">KPI Evento</h2>
          <p className="text-gray-600 mt-1">Key Performance Indicators in tempo reale</p>
        </div>
        <Button onClick={exportKPI} variant="outline" data-testid="export-kpi-button">
          <Download className="w-4 h-4 mr-2" />
          Esporta KPI
        </Button>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-600">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Biglietti Venduti</CardTitle>
            <Ticket className="w-4 h-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-sold">
              {stats.total_sold}
            </div>
            <p className="text-xs text-gray-600 mt-1">su {stats.total_available} disponibili</p>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full" 
                style={{ width: `${Math.min(soldOutPercentage, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs font-medium text-blue-600 mt-1">{soldOutPercentage.toFixed(1)}% venduto</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-600">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Revenue Totale</CardTitle>
            <DollarSign className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-revenue">
              €{stats.total_revenue.toFixed(2)}
            </div>
            <p className="text-xs text-gray-600 mt-1">Media: €{avgTicketPrice.toFixed(2)}/biglietto</p>
            {projectedRevenue > stats.total_revenue && (
              <p className="text-xs text-green-600 mt-1">
                Proiezione sold-out: €{projectedRevenue.toFixed(2)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-600">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Check-in</CardTitle>
            <Users className="w-4 h-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-checkins">
              {stats.total_checkins}
            </div>
            <p className="text-xs text-gray-600 mt-1">su {stats.total_sold} venduti</p>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full" 
                style={{ width: `${Math.min(checkinRate, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs font-medium text-purple-600 mt-1">{checkinRate.toFixed(1)}% check-in rate</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-600">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Performance</CardTitle>
            <Target className="w-4 h-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {soldOutPercentage >= 90 ? 'A+' : soldOutPercentage >= 70 ? 'A' : soldOutPercentage >= 50 ? 'B' : soldOutPercentage >= 30 ? 'C' : 'D'}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {soldOutPercentage >= 90 ? 'Eccellente' : soldOutPercentage >= 70 ? 'Ottimo' : soldOutPercentage >= 50 ? 'Buono' : soldOutPercentage >= 30 ? 'Discreto' : 'Da migliorare'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Basato su vendite e check-in
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Best Seller */}
      {bestSeller && (
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-full">
                <Award className="w-8 h-8 text-yellow-900" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Biglietto Più Venduto</h3>
                <p className="text-2xl font-bold text-yellow-900 mt-1">{bestSeller.name}</p>
                <p className="text-sm text-gray-700 mt-1">
                  {bestSeller.quantity_sold} venduti • €{(bestSeller.price * bestSeller.quantity_sold).toFixed(2)} di revenue
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-yellow-900">
                  {((bestSeller.quantity_sold / stats.total_sold) * 100).toFixed(0)}%
                </div>
                <p className="text-xs text-gray-700">del totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Check-ins by Zone */}
      {Object.keys(stats.checkins_by_zone).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Check-in per Zona
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(stats.checkins_by_zone).map(([zoneName, count]) => {
                const percentage = stats.total_checkins > 0 ? (count / stats.total_checkins) * 100 : 0;
                return (
                  <div key={zoneName}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-medium">{zoneName}</span>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-blue-600">{count}</span>
                        <span className="text-sm text-gray-600 ml-2">({percentage.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket Types Performance */}
      {stats.ticket_types.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Performance Tipologie Biglietti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipologia</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prezzo</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Venduti</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% Vendita</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.ticket_types.map(tt => {
                    const sellPercentage = tt.quantity_available > 0 ? (tt.quantity_sold / tt.quantity_available) * 100 : 0;
                    const ttRevenue = tt.price * tt.quantity_sold;
                    
                    return (
                      <tr key={tt.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{tt.name}</div>
                          <div className="text-xs text-gray-600">{tt.description}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">€{tt.price.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-semibold text-gray-900">{tt.quantity_sold}</div>
                          <div className="text-xs text-gray-600">/ {tt.quantity_available}</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-semibold text-gray-900">{sellPercentage.toFixed(1)}%</div>
                          <div className="w-20 bg-gray-200 rounded-full h-2 ml-auto mt-1">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(sellPercentage, 100)}%` }}
                            ></div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-green-600">
                          €{ttRevenue.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {sellPercentage >= 100 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              SOLD OUT
                            </span>
                          ) : sellPercentage >= 80 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Quasi Esaurito
                            </span>
                          ) : sellPercentage >= 50 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              In Vendita
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Disponibile
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revenue Medio</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">€{avgTicketPrice.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">per biglietto</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tasso Conversione</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{soldOutPercentage.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-1">obiettivo 100%</p>
              </div>
              <Target className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Check-in Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{checkinRate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-1">partecipazione</p>
              </div>
              <Clock className="w-10 h-10 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
